import React, { useEffect, useRef, useState } from 'react';
import { cn } from '../../../lib/utils';

interface MockupDisplayProps {
  children: React.ReactNode;
  isEven: boolean;
  isVisible: boolean;
}

export const MockupDisplay: React.FC<MockupDisplayProps> = ({ children, isEven, isVisible }) => {
  return (
    <div
      className="flex-1 w-full lg:w-auto transition-all duration-700"
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible
          ? 'translateX(0) scale(1)'
          : `translateX(${isEven ? '50px' : '-50px'}) scale(0.95)`,
      }}
    >
      <div 
        className="relative rounded-2xl overflow-hidden shadow-2xl glow-card z-depth-2 inner-border-glow hover-scale"
        style={{
          transition: 'transform 0.3s ease'
        }}
      >
        {children}
      </div>
    </div>
  );
};

interface MockupContentProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  isEven: boolean;
  isVisible: boolean;
}

export const MockupContent: React.FC<MockupContentProps> = ({ 
  title, 
  description, 
  icon, 
  isEven, 
  isVisible 
}) => {
  return (
    <div
      className={cn(
        'flex-1 space-y-8 text-center lg:text-left opacity-0',
        isVisible && (isEven ? 'animate-slide-in-left' : 'animate-slide-in-right')
      )}
    >
      <div className="flex flex-col lg:flex-row items-center lg:items-center gap-4">
        <div className="w-16 h-16 rounded-2xl bg-blue-600/10 flex items-center justify-center flex-shrink-0 inner-light soft-highlight">
          {icon}
        </div>
        <h3 className="text-3xl font-bold text-white font-['Sora']">{title}</h3>
      </div>
      <p className="text-xl text-gray-400 leading-relaxed">{description}</p>
    </div>
  );
};

export function useIntersectionObserver(threshold = 0.2): [React.RefObject<HTMLDivElement>, boolean] {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        // Update visibility based on current intersection state
        // This allows animations to reverse when scrolling back up
        setIsVisible(entry.isIntersecting);
      },
      {
        threshold,
      }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.disconnect();
      }
    };
  }, [threshold]);

  return [ref, isVisible];
}